import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { 
  FeedbackCategory, 
  FeedbackPriority, 
  IFeedback, 
  IFeedbackAttachment,
  feedbackCategoryOptions,
  feedbackPriorityOptions
} from './feedback.model';
import { CustomerClient } from 'src/app/client/customer.client';
import { Session } from 'src/app/common/entity/entities';

@Component({
  selector: 'gs-share-feedback-modal',
  templateUrl: './gs-share-feedback-modal.component.html',
  styleUrls: ['./gs-share-feedback-modal.component.scss']
})
export class GsShareFeedbackModalComponent implements OnInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() feedbackSubmitted = new EventEmitter<IFeedback>();

  constructor(private shareFeedbackClient: CustomerClient) {}

  currentStep = 1;
  totalSteps = 3;
  showSuccess = false;

  // Form data
  summary = '';
  details = '';
  selectedCategory: FeedbackCategory | null = null;
  selectedPriority: FeedbackPriority = FeedbackPriority.Low;
  attachments: IFeedbackAttachment[] = [];

  // Options
  categoryOptions = feedbackCategoryOptions;
  priorityOptions = feedbackPriorityOptions;

  // Allowed file types
  allowedFileTypes = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ];
  maxFileSize = 10 * 1024 * 1024; // 10MB

  ngOnInit(): void {
    this.resetForm();
  }

  onOverlayClick(event: MouseEvent): void {
    this.closeModal();
  }

  onClose(): void {
    this.closeModal();
  }

  closeModal(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetForm();
  }

  resetForm(): void {
    this.currentStep = 1;
    this.summary = '';
    this.details = '';
    this.selectedCategory = null;
    this.selectedPriority = FeedbackPriority.Low;
    this.attachments = [];
    this.showSuccess = false;
  }

  // Navigation
  canProceedToNextStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.summary.trim().length > 0;
      case 2:
        return true; // Details and category are optional
      case 3:
        return true; // Priority has default selection
      default:
        return false;
    }
  }

  nextStep(): void {
    if (this.canProceedToNextStep() && this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step;
    }
  }

  // File handling
  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
    }
    // Reset input to allow selecting same file again
    input.value = '';
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (event.dataTransfer?.files) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }
  
  handleFiles(files: File[]): void {
    for (const file of files) {
      if (!this.allowedFileTypes.includes(file.type)) {
        console.warn(`File type not allowed: ${file.type}`);
        continue;
      }
      if (file.size > this.maxFileSize) {
        console.warn(`File too large: ${file.name}`);
        continue;
      }
      
      const attachment: IFeedbackAttachment = {
        file: file,
        name: file.name,
        type: file.type,
        size: file.size
      };
      this.attachments.push(attachment);
    }
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  getFileIcon(type: string): string {
    if (type.includes('pdf')) return 'fa fa-file-pdf';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'fa fa-file-excel';
    if (type.includes('image')) return 'fa fa-file-image';
    return 'fa fa-file';
  }

  getFileIconColor(type: string): string {
    if (type.includes('pdf')) return '#ef4444';
    if (type.includes('excel') || type.includes('spreadsheet')) return '#22c55e';
    if (type.includes('image')) return '#3b82f6';
    return '#6b7280';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Submission
  submitFeedback(): void {
    if (!this.canProceedToNextStep()) return;

    const feedback: IFeedback = {
      summary: this.summary.trim(),
      details: this.details.trim() || undefined,
      category: this.selectedCategory || undefined,
      priority: this.selectedPriority,
      attachments: this.attachments.length > 0 ? this.attachments : undefined
    };

    // Create FormData for file uploads
    const formData = new FormData();
    formData.append('summary', feedback.summary);
    if (feedback.details) {
      formData.append('details', feedback.details);
    }
    if (feedback.category) {
      formData.append('category', feedback.category);
    }
    formData.append('priority', feedback.priority);
    
    // Add attachments
    if (feedback.attachments) {
      feedback.attachments.forEach((attachment, index) => {
        formData.append(`attachments`, attachment.file, attachment.name);
      });
    }

    // Close modal immediately and show spinner
    this.visible = false;
    this.visibleChange.emit(false);
    Session.showSpinner = true;

    this.shareFeedbackClient.sendFeedback('Sending feedback...', formData)
      .then((response) => {
        this.feedbackSubmitted.emit(feedback);
        Session.showSpinner = false;
        this.showSuccess = true;
        this.visible = true;
        this.visibleChange.emit(true);
      })
      .catch((error) => {
        console.error('Error sending feedback:', error);
        Session.showSpinner = false;
        alert('Failed to send feedback. Please try again later.');
      });
  }

  onSuccessClose(): void {
    this.closeModal();
  }

  getCategoryIcon(category: FeedbackCategory): string {
    const option = this.categoryOptions.find(c => c.value === category);
    return option?.svgPath || 'M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
  }
}
